import type { ChatSession } from "./chatStore";

export const CHAT_SESSION_STORAGE_KEY = "clank.chat.sessions.v1";
export const CHAT_SESSION_STORAGE_LIMIT = 20;

/**
 * Restores persisted sessions from oldest to newest.
 *
 * Object property order is intentional here: writes below build the object in
 * recency order, which lets a reload reconstruct the same eviction order
 * without adding metadata to every session.
 */
export const readPersistedChatSessions = (storage: Storage): [string, ChatSession][] => {
  try {
    const raw = storage.getItem(CHAT_SESSION_STORAGE_KEY);
    if (!raw) return [];
    const stored = JSON.parse(raw) as unknown;
    if (stored === null || typeof stored !== "object" || Array.isArray(stored)) return [];
    return Object.entries(stored as Record<string, ChatSession>).slice(-CHAT_SESSION_STORAGE_LIMIT);
  } catch {
    return [];
  }
};

/**
 * Stores the 20 most recently touched completed chats. If the browser's
 * per-origin session-storage quota is smaller than their combined transcripts,
 * retry with the oldest chat removed until the newest chats fit.
 */
export const writePersistedChatSessions = (
  storage: Storage,
  sessionsByRecency: [string, ChatSession][],
): void => {
  const candidates = sessionsByRecency
    .filter(([, session]) => session.status !== "streaming")
    .slice(-CHAT_SESSION_STORAGE_LIMIT);

  if (candidates.length === 0) {
    try {
      storage.setItem(CHAT_SESSION_STORAGE_KEY, "{}");
    } catch {
      // Storage can be unavailable altogether; the in-memory store still works.
    }
    return;
  }

  for (let first = 0; first < candidates.length; first += 1) {
    try {
      storage.setItem(
        CHAT_SESSION_STORAGE_KEY,
        JSON.stringify(Object.fromEntries(candidates.slice(first))),
      );
      return;
    } catch {
      // Quotas are browser- and origin-specific. Prefer progressively evicting
      // our oldest transcripts to assuming a fixed number of available bytes.
    }
  }
};
